<template>
    <div class="register-head">
        <div class="register-head__left">
            <img
                v-if="currentStep === 0 || (currentStep === 1 && !selectedExam)"
                class="register-head__logo register-head__logo--desktop"
                src="@/assets/logos/logo.svg"
                alt="Pocket Prep Logo"
            >
            <img
                v-if="currentStep === 0 || (currentStep === 1 && !selectedExam)"
                class="register-head__logo register-head__logo--mobile"
                src="@/assets/logos/logo-square.svg"
                alt="Pocket Prep Logo"
            >
            <div
                v-else
                class="register-head__back"
                tabindex="0"
                role="button"
                aria-label="Back"
                @keydown.enter="emitBack"
                @click="emitBack"
                @mousedown.prevent
            >
                <Icon type="arrow" />
            </div>
        </div>
        <div class="register-head__steps">
            <div
                class="register-head__step"
                :class="{
                    'register-head__step--active': currentStep === 1 || currentStep === 2,
                    'register-head__step--complete': currentStep > 2,

                }"
                tabindex="-1"
                :aria-label="`${currentStep === 1 || currentStep === 2
                    ? 'Current '
                    : currentStep > 2
                        ? 'Completed '
                        : ''
                }Step 1 Choose Exam`"
            >
                <span v-if="currentStep <= 2" aria-hidden="true">1</span>
                <span v-else-if="currentStep > 2" aria-hidden="true"><Icon type="check" /></span>
                <div aria-hidden="true">
                    <div class="register-head__step--desktop">
                        Choose
                    </div> Exam
                </div>
            </div>
            <div
                class="register-head__step"
                :class="{
                    'register-head__step--active': currentStep === 3,
                    'register-head__step--complete': currentStep > 3,
                }"
                tabindex="-1"
                :aria-label="`${currentStep === 3
                    ? 'Current '
                    : currentStep > 3
                        ? 'Completed '
                        : ''
                }Step 2 Account Details`"
            >
                <span v-if="currentStep <= 3" aria-hidden="true">2</span>
                <span v-else-if="currentStep > 3" aria-hidden="true"><Icon type="check" /></span>
                <div aria-hidden="true">
                    Account <div class="register-head__step--desktop">
                        Details
                    </div>
                </div>
            </div>
            <div
                v-if="!isFreeExam"
                class="register-head__step"
                :class="{
                    'register-head__step--active': currentStep === 4,
                    'register-head__step--complete': currentStep > 4,
                }"
                tabindex="-1"
                :aria-label="`${currentStep >= 4
                    ? 'Current '
                    : ''
                }Step 3 Choose Plan`"
            >
                <span v-if="currentStep <= 4" aria-hidden="true">3</span>
                <span v-else-if="currentStep > 4" aria-hidden="true"><Icon type="check" /></span>
                <div aria-hidden="true">
                    <div class="register-head__step--desktop">
                        Choose
                    </div> Plan
                </div>
            </div>
        </div>
        <div class="register-head__links">
            <template v-if="currentStep === 0 || (currentStep === 1 && !selectedExam)">
                <RouterLink
                    v-slot="{ href, navigate }"
                    custom
                    :to="{ name: 'sign-in' }"
                >
                    <PocketLink
                        class="register-head__link"
                        type="tertiary-small"
                        :href="href"
                        @click="navigate"
                        @mousedown.prevent
                    >
                        I have an account
                    </PocketLink>
                </RouterLink>
                <span class="register-head__middot">•</span>
            </template>
            <PocketButton
                class="register-head__link register-head__link-help"
                type="tertiary-small"
                @click="showIntercom"
                @keypress.enter="showIntercom"
                @mousedown.prevent
            >
                Help
            </PocketButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import UIKit from '@pocketprep/ui-kit'
import { isSupportWindow } from '@/utils'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'

const {
    Icon,
    Link: PocketLink,
    Button: PocketButton,
} = UIKit

withDefaults(defineProps<{
    currentStep: number
    selectedExam: Study.Class.ExamMetadataJSON | ClientExam | null
    isFreeExam?: boolean
}>(), {
    isFreeExam: false,
})

const emit = defineEmits<{
    'back': []
}>()

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}

const emitBack = () => {
    emit('back')
}
</script>

<style lang="scss" scoped>
.register-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    &__logo {
        height: 27px;

        &--desktop {
            @include breakpoint(black-bear) {
                display: none;
            }
        }

        &--mobile {
            display: none;

            @include breakpoint(black-bear) {
                display: block;
            }
        }
    }

    &__steps {
        display: flex;
    }

    &__step {
        font-size: 13px;
        line-height: 21px;
        color: $slate-01;
        margin-right: 32px;
        position: relative;

        @include breakpoint(black-bear) {
            margin-right: 24px;
        }

        &::after {
            content: '';
            right: -19px;
            top: 6px;
            width: 5px;
            height: 8px;
            position: absolute;
            background: url('@/assets/onboarding/stepper.svg') no-repeat center center;

            @include breakpoint(black-bear) {
                right: -15px;
            }
        }

        &:last-child {
            margin-right: 0;

            &::after {
                display: none;
            }
        }

        &--desktop {
            display: inline;

            @include breakpoint(black-bear) {
                display: none !important;
            }
        }

        span {
            font-weight: 700;
            width: 16px;
            margin: 0 0 0 2px;
            display: inline-block;
        }

        div {
            display: inline-block;
        }

        &--active {
            color: $brand-black;
            font-weight: 600;

            span {
                color: $brand-blue;
            }
        }

        &--complete {
            span {
                margin: 0 2px 0 0;

                svg {
                    width: 16px;
                    height: 13px;
                }
            }

            div {
                text-decoration: line-through;
            }
        }
    }

    &__left {
        display: flex;
        width: 190px;

        @include breakpoint(brown-bear) {
            width: 136px;
        }

        @include breakpoint(black-bear) {
            width: 60px;
        }
    }

    &__back {
        color: $brand-blue;
        cursor: pointer;
        outline: none;
        position: relative;

        &:hover,
        &:focus {
            color: $brand-black;
        }

        &:focus::before {
            content: "";
            border: 1px solid $brand-black;
            width: 27px;
            height: 26px;
            top: -6px;
            left: -4px;
            border-radius: 4px;
            box-sizing: border-box;
            position: absolute;
        }

        svg {
            transform: rotate(180deg);
            width: 19px;
            height: 14px;
        }
    }

    &__links {
        font-size: 14px;
        line-height: 19px;
        width: 190px;
        text-align: right;

        @include breakpoint(brown-bear) {
            width: 136px;
        }

        @include breakpoint(black-bear) {
            width: 60px;
        }

        span {
            @include breakpoint(brown-bear) {
                display: none;
            }
        }
    }

    &__middot {
        color: $pewter;
    }

    &__link {
        margin: 0 8px;
        display: inline-block;

        @include breakpoint(brown-bear) {
            display: none;
        }

        &:first-child {
            margin-left: 0;
        }

        &:last-child {
            margin-right: 0;
        }

        &-help {
            display: inline-block;
        }
    }
}
</style>
